// Stock report: every drug and medical consumable — how much is held, how much is in
// the stock cupboards to top up from, how much every line requires — with the lines
// that are short and the batches that are expired, expiring or recalled
// (GET /stock/report). The expiry verdicts are the inventory page's own
// (stockRules.expiryFlag, with the type's grace), so the two can't disagree about
// what needs replacing.
import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box, Button, Chip, IconButton, MenuItem, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup,
  Tooltip, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import HistoryIcon from "@mui/icons-material/History";
import { API_BASE_URL } from "../config";
import { STOCK_ROLE_LABEL } from "../constants";
import { expiryFlag } from "../stockRules";
import { exportStockReport } from "../exportExcel";

const SHORT = "short";
const EXPIRED = "expired";
const EXPIRING = "expiring";
const RECALLED = "recalled";
const PROBLEM_LABEL = { [SHORT]: "Short", [EXPIRED]: "Expired", [EXPIRING]: "Expiring", [RECALLED]: "Recalled" };

// Out-of-use stock (set aside for disposal) is still held, but it can't be used, so
// it isn't counted against what the bags need or flagged as expiring.
const inUse = (line) => line.stock_role !== "quarantine";

// One type's figures, derived from its lines.
const summarise = (t) => {
  const grace = Number(t.expiry_grace_years) || 0;
  const usable = t.lines.filter(inUse);
  const holdings = usable.flatMap(l => l.holdings);
  const sumWhere = (pred) => holdings.filter(pred).reduce((n, h) => n + h.quantity, 0);
  const flag = (h) => expiryFlag(h.expiry_date, grace)?.severity;
  const shortLines = usable.filter(l => l.stock_role !== "store" && l.required_quantity != null
                                        && l.quantity < l.required_quantity);
  return {
    expired: sumWhere(h => flag(h) === "error"),
    expiring: sumWhere(h => flag(h) === "warning"),
    recalled: t.lines.flatMap(l => l.holdings).filter(h => h.recalled).reduce((n, h) => n + h.quantity, 0),
    shortLines,
    shortBy: shortLines.reduce((n, l) => n + (l.required_quantity - l.quantity), 0),
  };
};

export default function StockReport() {
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [problems, setProblems] = useState([]);
  const [open, setOpen] = useState({});

  useEffect(() => {
    axios.get(`${API_BASE_URL}/stock/report`).then(res => setTypes(res.data))
      .finally(() => setLoading(false));
  }, []);

  const rows = types.map(t => ({ t, s: summarise(t) }))
    .filter(({ t }) => !category || t.category === category)
    .filter(({ s }) => problems.length === 0 || problems.some(p =>
      (p === SHORT && s.shortLines.length) || (p === EXPIRED && s.expired)
      || (p === EXPIRING && s.expiring) || (p === RECALLED && s.recalled)));

  const flagText = (t, h) => {
    const f = expiryFlag(h.expiry_date, Number(t.expiry_grace_years) || 0);
    return [f?.message, h.recalled ? "Recalled" : null].filter(Boolean).join("; ");
  };

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/inventory-items")}>Inventory</Button>
        <Typography variant="h4" sx={{ flex: 1 }}>Stock report</Typography>
        <Button variant="outlined" startIcon={<FileDownloadIcon />} disabled={rows.length === 0}
                onClick={() => exportStockReport(rows.map(r => r.t), { flag: flagText })}>
          Export to Excel
        </Button>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "center" } }}>
        <TextField label="Category" select size="small" sx={{ minWidth: 160 }} value={category}
                   onChange={e => setCategory(e.target.value)}>
          <MenuItem value=""><em>Drugs and medical</em></MenuItem>
          <MenuItem value="Drugs">Drugs</MenuItem>
          <MenuItem value="Medical">Medical</MenuItem>
        </TextField>
        <ToggleButtonGroup size="small" color="warning" value={problems} onChange={(_, v) => setProblems(v)}>
          {[SHORT, EXPIRED, EXPIRING, RECALLED].map(p => (
            <ToggleButton key={p} value={p}>{PROBLEM_LABEL[p]}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary" sx={{ ml: { sm: "auto" } }}>
          {loading ? "Loading…" : `${rows.length} of ${types.length}`}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Short counts every line below its required quantity, except in stock cupboards.
        Stock set aside for disposal is listed, but not counted as usable or expiring.
      </Typography>

      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "action.hover" } }}>
                <TableCell sx={{ width: 40 }} />
                <TableCell>Item</TableCell>
                <TableCell align="right">Held</TableCell>
                <TableCell align="right">In cupboards</TableCell>
                <TableCell align="right">Required in kit</TableCell>
                <TableCell>Needs attention</TableCell>
                <TableCell sx={{ width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(({ t, s }) => (
                <Fragment key={t.type_id}>
                  <TableRow hover onClick={() => setOpen(o => ({ ...o, [t.type_id]: !o[t.type_id] }))}
                            sx={{ cursor: "pointer" }}>
                    <TableCell>
                      <IconButton size="small">{open[t.type_id] ? <ExpandMoreIcon /> : <ChevronRightIcon />}</IconButton>
                    </TableCell>
                    <TableCell>
                      {t.name}
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{t.category}</Typography>
                    </TableCell>
                    <TableCell align="right">{t.held}</TableCell>
                    <TableCell align="right">{t.in_store}</TableCell>
                    <TableCell align="right">{t.required}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: "wrap" }}>
                        {s.shortLines.length > 0 && (
                          <Chip size="small" color="error"
                                label={`${s.shortLines.length} line${s.shortLines.length === 1 ? "" : "s"} short by ${s.shortBy}`} />
                        )}
                        {s.expired > 0 && <Chip size="small" color="error" variant="outlined" label={`${s.expired} expired`} />}
                        {s.expiring > 0 && <Chip size="small" color="warning" variant="outlined" label={`${s.expiring} expiring`} />}
                        {s.recalled > 0 && <Chip size="small" color="error" label={`${s.recalled} recalled`} />}
                        {t.in_quarantine > 0 && <Chip size="small" label={`${t.in_quarantine} awaiting disposal`} />}
                      </Stack>
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <Tooltip title="History of this item">
                        <IconButton size="small" onClick={() => navigate(`/stock-history?type_id=${t.type_id}`)}>
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  {open[t.type_id] && (
                    <TableRow>
                      <TableCell />
                      <TableCell colSpan={6} sx={{ pb: 2 }}>
                        {t.lines.length === 0 && (
                          <Typography variant="body2" color="text.secondary">Not stocked anywhere.</Typography>
                        )}
                        {t.lines.map(line => {
                          const short = line.stock_role !== "store" && line.required_quantity != null
                            && line.quantity < line.required_quantity;
                          return (
                            <Box key={line.item_id} sx={{ mb: 1 }}>
                              <Typography variant="body2" component="div" sx={{ fontWeight: 600 }}>
                                {line.where || "Not in a container"}
                                {line.label !== t.name ? ` — ${line.label}` : ""}
                                {line.stock_role && (
                                  <Chip size="small" label={STOCK_ROLE_LABEL[line.stock_role]} sx={{ ml: 1 }} />
                                )}
                              </Typography>
                              <Typography variant="body2" sx={short ? { color: "error.main" } : undefined}>
                                {line.quantity} held{line.required_quantity != null ? ` of ${line.required_quantity} required` : ""}
                              </Typography>
                              {line.holdings.map(h => {
                                const f = expiryFlag(h.expiry_date, Number(t.expiry_grace_years) || 0);
                                return (
                                  <Typography key={h.id} variant="caption" sx={{ display: "block",
                                    color: h.recalled || f?.severity === "error" ? "error.main"
                                      : f?.severity === "warning" ? "warning.main" : "text.secondary" }}>
                                    {h.quantity}{h.opened ? ` (${h.opened} opened)` : ""} · batch {h.batch_number || "—"} ·
                                    expires {h.expiry_date || "—"}{f ? ` — ${f.message}` : ""}{h.recalled ? " — RECALLED" : ""}
                                  </Typography>
                                );
                              })}
                            </Box>
                          );
                        })}
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ color: "text.secondary", py: 4 }}>
                    {types.length === 0 ? "No drug or medical types are defined." : "Nothing matches those filters."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Stack>
  );
}
